<template>
  <BModal v-model="showModalInternal" title="Nowy użytkownik" hide-footer scrollable>
    <div class="card shadow-sm mb-3">
      <div class="card-header bg-light">
        <h6 class="mb-0 text-primary fw-bold">
          <i class="fas fa-user-plus me-2"></i>Dane konta
        </h6>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-12">
            <label class="form-label required">Email</label>
            <input
              type="email"
              class="form-control"
              v-model="form.email"
              placeholder="Adres email"
            />
          </div>
          <div class="col-12">
            <label class="form-label required">Login</label>
            <input
              type="text"
              class="form-control"
              v-model="form.login"
              placeholder="Login użytkownika"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label required">Hasło</label>
            <input
              type="password"
              class="form-control"
              v-model="form.password"
              placeholder="Hasło"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label required">Powtórz hasło</label>
            <input
              type="password"
              class="form-control"
              :class="{ 'is-invalid': passwordMismatch }"
              v-model="form.confirmPassword"
              placeholder="Powtórz hasło"
            />
            <div v-if="passwordMismatch" class="invalid-feedback">Hasła nie są identyczne.</div>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-sm mb-3">
      <div class="card-header bg-light">
        <h6 class="mb-0 text-secondary fw-bold">
          <i class="fas fa-id-card me-2"></i>Dane osobowe (opcjonalnie)
        </h6>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Imię</label>
            <input type="text" class="form-control" v-model="form.firstName" placeholder="Imię" />
          </div>
          <div class="col-md-6">
            <label class="form-label">Nazwisko</label>
            <input type="text" class="form-control" v-model="form.lastName" placeholder="Nazwisko" />
          </div>
          <div class="col-12">
            <label class="form-label">Role</label>
            <BFormSelect
              v-model="form.roleNames"
              :options="rolesOptions"
              multiple
              size="5"
            />
            <small class="text-muted mt-1 d-block">Przytrzymaj Ctrl / Cmd, aby zaznaczyć wiele ról.</small>
          </div>
          <div class="col-12">
            <BFormCheckbox v-model="form.enabled">Konto aktywne</BFormCheckbox>
          </div>
        </div>
      </div>
    </div>

    <div v-if="validationError" class="alert alert-danger">{{ validationError }}</div>

    <div class="d-flex justify-content-end gap-2">
      <button type="button" class="btn btn-secondary" @click="showModalInternal = false">Anuluj</button>
      <button type="button" class="btn btn-primary" @click="createUser" :disabled="!canSubmit">
        <i class="fas fa-user-plus me-1"></i> Utwórz użytkownika
      </button>
    </div>
  </BModal>
</template>

<script>
import { BModal, BFormSelect, BFormCheckbox } from "bootstrap-vue-next";
import { useUserRbacManagerModule } from "@/state/pinia/userRbacManagerModule";

export default {
  name: "CreateUserModal",
  components: { BModal, BFormSelect, BFormCheckbox },
  props: {
    showModal: { type: Boolean, required: true },
    rolesCatalog: { type: Array, default: () => [] },
  },
  emits: ["update:showModal", "onUserCreated"],
  data() {
    return {
      showModalInternal: this.showModal,
      form: {
        email: "",
        login: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
        enabled: true,
        roleNames: ["ROLE_USER"],
      },
      validationError: "",
    };
  },
  computed: {
    rbacModule() {
      return useUserRbacManagerModule();
    },
    rolesOptions() {
      if (this.rolesCatalog && this.rolesCatalog.length > 0) {
        return this.rolesCatalog.map((role) => {
          const name = role?.name || role?.key || role?.roleKey || role;
          return { value: name, text: name };
        });
      }
      return [
        { value: "ROLE_USER", text: "ROLE_USER" },
        { value: "ROLE_MODERATOR", text: "ROLE_MODERATOR" },
        { value: "ROLE_ADMIN", text: "ROLE_ADMIN" },
      ];
    },
    passwordMismatch() {
      return (
        this.form.password.length > 0 &&
        this.form.confirmPassword.length > 0 &&
        this.form.password !== this.form.confirmPassword
      );
    },
    canSubmit() {
      return (
        this.form.email.trim() &&
        this.form.login.trim() &&
        this.form.password.trim() &&
        !this.passwordMismatch
      );
    },
  },
  watch: {
    showModal(newValue) {
      this.showModalInternal = newValue;
      if (newValue) {
        this.resetForm();
      }
    },
    showModalInternal(newValue) {
      this.$emit("update:showModal", newValue);
    },
  },
  methods: {
    resetForm() {
      this.form = {
        email: "",
        login: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
        enabled: true,
        roleNames: ["ROLE_USER"],
      };
      this.validationError = "";
    },
    createUser() {
      this.validationError = "";
      if (!this.form.email.trim()) {
        this.validationError = "Email jest wymagany.";
        return;
      }
      if (!this.form.login.trim()) {
        this.validationError = "Login jest wymagany.";
        return;
      }
      if (!this.form.password.trim()) {
        this.validationError = "Hasło jest wymagane.";
        return;
      }
      if (this.form.password !== this.form.confirmPassword) {
        this.validationError = "Hasła nie są identyczne.";
        return;
      }

      const payload = {
        email: this.form.email.trim(),
        login: this.form.login.trim(),
        password: this.form.password,
        firstName: this.form.firstName.trim() || null,
        lastName: this.form.lastName.trim() || null,
        enabled: this.form.enabled,
        roleNames: this.form.roleNames.length > 0 ? this.form.roleNames : ["ROLE_USER"],
      };

      this.rbacModule.httpAdminCreateUser(payload).then(() => {
        this.$emit("onUserCreated");
        this.showModalInternal = false;
      });
    },
  },
};
</script>

<style scoped>
.required:after {
  content: " *";
  color: red;
}
</style>
